import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { camBasis, createWorldObject, updateWorldObject, deleteWorldObject } from '../services/worldObjects';
import { listRoles, syncRoster } from '../services/ensemble';
import { POSES, POSE_KEYS } from '../poses';

// InteriorResidentPanel — put an avatar inside a room, move it by eye, save it.
//
// Buttons mutate store state DIRECTLY. The previous version dispatched synthetic
// keydown events to reuse an in-scene handler, which quietly broke: these are
// first-person rooms whose camera already listens for arrows and WASD on window,
// so every "move" drove the camera and the body together and nothing appeared to
// happen. Nothing here touches the keyboard.
//
// Save writes to world_objects, so an arrangement is a row rather than a
// deploy. Until migration 261 has run the API returns empty and saving 500s —
// the panel says so rather than failing silently.
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;

const panel = {
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 40,
  width: 216, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.82)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};
const arrow = { ...btn, padding: '9px 0' };
const r2 = (n) => Math.round(n * 100) / 100;
const r3 = (n) => Math.round(n * 1000) / 1000;

export default function InteriorResidentPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'iresident';
  const interior = useWorldStore((s) => s.interior);
  const objects = useWorldStore((s) => s.worldObjects);
  const sel = useWorldStore((s) => s.worldObjectSel);
  const dirty = useWorldStore((s) => s.worldObjectDirty);
  const setSel = useWorldStore((s) => s.setWorldObjectSel);
  const patch = useWorldStore((s) => s.patchWorldObject);
  const addObj = useWorldStore((s) => s.addWorldObject);
  const removeObj = useWorldStore((s) => s.removeWorldObject);
  const markSaved = useWorldStore((s) => s.markWorldObjectSaved);

  const [roster, setRoster] = useState([]);
  const [roles, setRoles] = useState([]);
  const [fine, setFine] = useState(false);
  const [pick, setPick] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ON) return;
    fetch('/models/avatars/roster.json', { cache: 'no-store' })
      .then((r) => r.json()).then((d) => setRoster((d.npcs || []).filter((n) => n.rigged))).catch(() => setRoster([]));
  }, [ON]);

  // The JOBS that can be worked in this room (migration 284). Still two lists on
  // one panel, but they now answer questions that do not overlap: the roster
  // above is WHO (a body, and the person it belongs to, chosen together), this
  // one is WHAT THEY DO. Nothing here carries a name or a voice, so there is no
  // second identity to keep in sync with the first.
  useEffect(() => {
    if (!ON || !interior) return;
    listRoles(interior).then(setRoles);
  }, [ON, interior]);

  if (!ON) return null;
  if (!interior) {
    return (
      <div style={panel}>
        <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>Interior avatar</div>
        <div style={{ opacity: 0.65, fontSize: 11.5, lineHeight: 1.4 }}>Step into a room first — this places a body inside an interior.</div>
      </div>
    );
  }

  const mine = objects.filter((o) => o.zone_id === interior && o.kind === 'resident');
  const cur = mine.find((o) => o.object_id === sel) || null;
  const step = fine ? 0.02 : 0.1;

  // Move along the camera's own axes: forward is where you look, right is its
  // perpendicular. World-axis movement is unusable in a room you turn around in.
  const move = (axis, dir) => {
    if (!cur) return;
    const [x, y, z] = cur.pos;
    const d = step * dir;
    if (axis === 'across') patch(cur.object_id, { pos: [r2(x + camBasis.rx * d), y, r2(z + camBasis.rz * d)] });
    else if (axis === 'depth') patch(cur.object_id, { pos: [r2(x + camBasis.fx * d), y, r2(z + camBasis.fz * d)] });
    else patch(cur.object_id, { pos: [x, r2(y + d), z] });
  };
  const turn = (dir) => cur && patch(cur.object_id, { rotY: r2(cur.rotY + 0.1 * dir) });
  const resize = (dir) => cur && patch(cur.object_id, { size: Math.max(0.2, r3(cur.size + (fine ? 0.01 : 0.04) * dir)) });

  const addOne = () => {
    if (!pick) return;
    // ARRIVES IN FRONT OF YOU, not at the room's spawn point.
    //
    // Placing at the spawn is only "where you can see it" for the one second
    // before you walk anywhere. In a room the size of the shop it puts the body
    // across the floor behind a shelf, and the first thing you have to do after
    // adding one is go and find it.
    //
    // 1.6 units ahead of the camera, on the floor. Far enough not to be inside
    // your own head, close enough to nudge without walking.
    const ahead = 1.6;
    let x = camBasis.px + camBasis.fx * ahead;
    let z = camBasis.pz + camBasis.fz * ahead;

    // ── WORLD SPACE IN, ROOM SPACE OUT ─────────────────────────────────────
    //
    // The camera reports where it is in the WORLD. A resident is saved in ROOM
    // coordinates — the same space the room's own spawn is authored in — and
    // MeshInterior renders that space inside a group that is both MOVED to the
    // venue's street address and TURNED to face its street:
    //
    //     world = anchor + R(rotY) · local
    //
    // so recovering `local` means undoing both, in that order. Subtracting the
    // anchor alone was half the transform, and with this store turned 270° the
    // remaining rotation threw every placement a quarter turn out — through a
    // wall, or outside the building entirely. Which looks exactly like nothing
    // being placed at all.
    const st = useWorldStore.getState();
    const sited = ROOMS[interior]?.placement === 'sited';
    const anchor = sited ? st.venueAnchors?.[interior] : null;
    // The room's facing, resolved the same way MeshInterior resolves it — draft,
    // then saved placement, then the rooms.js default.
    const roomRot = sited
      ? (st.venuePlaceDraft?.[interior]?.rotY ?? st.venueAnchorOverrides?.[interior]?.rotY ?? ROOMS[interior]?.modelRotY ?? 0)
      : 0;
    if (anchor) { x -= anchor[0]; z -= anchor[2]; }
    if (roomRot) {
      const c = Math.cos(-roomRot); const s2 = Math.sin(-roomRot);
      const rx2 = x * c + z * s2;
      const rz2 = -x * s2 + z * c;
      x = rx2; z = rz2;
    }

    // Face the camera, in the room's frame for the same reason.
    const facing = Math.atan2(-camBasis.fx, -camBasis.fz) - roomRot;
    // 1.7 was a person in METRES; this world's human scale is ~0.75 eye height
    // (PlanetScene EYE), which makes a 1.7 body more than twice the height of
    // everyone else in the city.
    // THE BODY AND THE PERSON, WRITTEN TOGETHER.
    //
    // `ref` is the model; `data.avatarId` is who it is. They are set in the same
    // action, from the same roster entry, so they cannot drift — which is the
    // bug this replaces: the body was picked here while the part carried its own
    // avatar_id, and the two could disagree with nothing to catch it (place
    // Rebel, point the part at Cosmina, get Rebel's body in Cosmina's voice).
    // The server reads avatarId from here and never from the request body.
    const who = roster.find((n) => n.slug === pick);
    if (!who?.avatarId) setMsg(`${pick} has no avatarId in roster.json — placed, but it cannot hold a job`);
    addObj({
      zone_id: interior, kind: 'resident', ref: pick, slot: mine.length,
      pos: [r2(x), 0, r2(z)], rotY: r2(facing), size: 0.85,
      data: who?.avatarId ? { avatarId: who.avatarId } : null,
    });
  };

  const save = async () => {
    if (!cur) return;
    setBusy(true); setMsg('');
    try {
      // `data` travels with the save — it carries the casting (`data.actor`), and
      // a save that dropped it would silently un-cast a body every time somebody
      // nudged it half a metre.
      const body = { zone_id: interior, kind: 'resident', ref: cur.ref, slot: cur.slot ?? 0, pos: cur.pos, rotY: cur.rotY, size: cur.size, data: cur.data ?? null };
      // A local or authored id means it has never been a row — create it.
      const isNew = typeof cur.object_id === 'string';
      const saved = isNew ? await createWorldObject(body) : await updateWorldObject(cur.object_id, body);
      if (saved?.object_id) { patch(cur.object_id, { object_id: saved.object_id }); markSaved(cur.object_id); setSel(saved.object_id); markSaved(saved.object_id); }
      setMsg('saved');
    } catch (e) {
      setMsg(e.message.includes('500') ? 'save failed — has migration 261 run?' : e.message);
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  const remove = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      if (typeof cur.object_id === 'number') await deleteWorldObject(cur.object_id);
      removeObj(cur.object_id);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const isDirty = cur && dirty.includes(cur.object_id);

  return (
    <div style={panel}>
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>
        Interior avatar · {ROOMS[interior]?.name || interior}
      </div>

      <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
        <select value={pick} onChange={(e) => setPick(e.target.value)}
          style={{ flex: 1, background: 'rgba(255,178,94,0.1)', color: '#ffd27d', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '6px', font: '12px system-ui' }}>
          <option value="">add avatar…</option>
          {roster.map((n) => <option key={n.slug} value={n.slug} style={{ background: '#1a1109' }}>{n.name || n.slug}</option>)}
        </select>
        <button style={{ ...btn, width: 40 }} onClick={addOne} disabled={!pick}>+</button>
      </div>

      {mine.length === 0 && <div style={{ opacity: 0.6, fontSize: 11.5, marginBottom: 8 }}>No one in here yet. Add someone above.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10, maxHeight: 112, overflowY: 'auto' }}>
        {mine.map((o) => (
          <button key={o.object_id} onClick={() => setSel(o.object_id)}
            style={{ ...btn, fontSize: 11.5, padding: '6px 8px', textAlign: 'left',
              background: o.object_id === sel ? '#ffb25e' : 'rgba(255,178,94,0.14)',
              color: o.object_id === sel ? '#1c1206' : '#ffd27d' }}>
            {o.ref}{o.data?.actor ? ` · ${o.data.actor.split('.').pop()}` : ''}{dirty.includes(o.object_id) ? ' •' : ''}
          </button>
        ))}
      </div>

      {cur && (
        <>
          {/* ── THE JOB ─────────────────────────────────────────────────────
              Khari, 2026-08-04: "my rock star can be my rock star working at the
              shop for the day... it still answers in its own voice."

              ONE DROPDOWN, and the one that went away is the one that could
              produce a wrong-voice bug. A role is a JOB — duties, tools, house
              rules, no face — so there is nothing here to cast a voice onto.
              Whoever you placed is who works it, and they arrive with their own
              name, voice, personality and memory of this fan. */}
          <div style={{ margin: '0 0 10px' }}>
            <div style={{ fontSize: 9.5, letterSpacing: 1.1, textTransform: 'uppercase', opacity: 0.55, marginBottom: 4 }}>works the job of</div>
            <select
              value={cur.data?.actor || ''}
              onChange={(e) => {
                const actor = e.target.value;
                const data = { ...(cur.data || {}) };
                if (actor) data.actor = actor; else delete data.actor;
                patch(cur.object_id, { data: Object.keys(data).length ? data : null });
              }}
              style={{ width: '100%', background: 'rgba(255,178,94,0.1)', color: '#ffd27d', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '6px', font: '12px system-ui' }}>
              <option value="">— nothing (scenery) —</option>
              {roles.map((r) => (
                <option key={r.key} value={r.key} style={{ background: '#1a1109' }}>
                  {r.role ? `${r.role} · ` : ''}{r.title || r.key}
                </option>
              ))}
            </select>

            {/* AND WHAT THEY ARE DOING WITH THEIR BODY. Deliberately a second
                question beside the job, not folded into it: the same barber
                stands at a station or sits waiting between customers, and the
                same person is a customer in a chair or browsing the shelf. Job
                is who they are; pose is what they are doing right now.

                Poses are a LOOK, so the numbers get tuned by eye rather than
                guessed — every bone is overridable live, e.g.
                ?pose.barber.RightArm=-38,0,-26 (degrees), no rebuild. */}
            <div style={{ fontSize: 9.5, letterSpacing: 1.1, textTransform: 'uppercase', opacity: 0.55, margin: '10px 0 4px' }}>and is</div>
            <select
              value={cur.data?.pose || ''}
              onChange={(e) => {
                const p = e.target.value;
                const data = { ...(cur.data || {}) };
                if (p) data.pose = p; else delete data.pose;
                patch(cur.object_id, { data: Object.keys(data).length ? data : null });
              }}
              style={{ width: '100%', background: 'rgba(255,178,94,0.1)', color: '#ffd27d', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '6px', font: '12px system-ui' }}>
              <option value="">— standing (idle loop) —</option>
              {POSE_KEYS.map((k) => (
                <option key={k} value={k} style={{ background: '#1a1109' }}>{POSES[k].label}</option>
              ))}
            </select>
            {cur.data?.pose && POSES[cur.data.pose] && (
              <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, lineHeight: 1.4 }}>
                {POSES[cur.data.pose].hint} Posed bodies hold their placed spot and facing — move the
                body to the chair, then face it the way the chair faces.
              </div>
            )}

            {/* WHO IS ON SHIFT — read-only, because it is not a separate choice
                any more. It is whoever this body belongs to, and the server
                resolves it from this row rather than from anything the browser
                sends. A placement with no avatarId was made before roles and
                just needs re-saving; say so, because a clerk with no personality
                and a clerk who is not there look identical from the shop floor. */}
            {cur.data?.actor && (() => {
              const r = roles.find((x) => x.key === cur.data.actor);
              const who = roster.find((n) => n.slug === cur.ref);
              const avatarId = cur.data?.avatarId || who?.avatarId || null;
              return (
                <div style={{ fontSize: 10, opacity: 0.72, marginTop: 5, lineHeight: 1.45 }}>
                  {!r && <div style={{ color: '#ff9d7d' }}>unknown job — check the role key</div>}
                  {avatarId
                    ? <div>on shift: <b>{who?.name || cur.ref}</b> — own voice, own personality</div>
                    : <div style={{ color: '#ff9d7d' }}>
                        no avatar on this placement — re-save it, or run migration 284
                      </div>}
                  {!!r?.tools?.length && <div style={{ opacity: 0.75 }}>tools: {r.tools.join(', ')}</div>}
                  {!!r?.speech?.length && <div style={{ opacity: 0.75 }}>speech: {r.speech.join(' · ')}</div>}
                </div>
              );
            })()}
            {!roles.length && <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4 }}>No jobs written for this room yet (migration 284).</div>}
          </div>

          {/* ATTACH AVATARS — for placements made before roles existed.
              Sends roster.json's slug→avatarId pairs, which the panel already
              has loaded, and the server fills only the blanks. This is here
              rather than in migration 284 because a hardcoded list of avatars
              goes stale every time one gets a rigged body (Khari: "this is only
              a partial list due to testing. there will be more"). Placements
              made from today already carry theirs. */}
          {!cur.data?.avatarId && !!roster.length && (
            <button style={{ ...btn, marginBottom: 8, fontSize: 11.5 }} disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const n = await syncRoster(roster);
                  setMsg(n ? `attached ${n} placement${n === 1 ? '' : 's'} — reload the room` : 'nothing needed attaching');
                } catch (e) { setMsg(e.message); } finally { setBusy(false); setTimeout(() => setMsg(''), 5000); }
              }}>
              {busy ? '…' : 'Attach avatars to old placements'}
            </button>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 4 }}>
            <span />
            <button style={arrow} onClick={() => move('depth', 1)}>↑</button>
            <span />
            <button style={arrow} onClick={() => move('across', -1)}>←</button>
            <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>move</div>
            <button style={arrow} onClick={() => move('across', 1)}>→</button>
            <span />
            <button style={arrow} onClick={() => move('depth', -1)}>↓</button>
            <span />
          </div>

          <div style={{ display: 'flex', gap: 5, margin: '8px 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => turn(1)}>⟲ turn</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => turn(-1)}>turn ⟳</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => resize(-1)}>− size</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => resize(1)}>size +</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 8px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => move('height', -1)}>▼ lower</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => move('height', 1)}>raise ▲</button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 8px', cursor: 'pointer', opacity: 0.9 }}>
            <input type="checkbox" checked={fine} onChange={(e) => setFine(e.target.checked)} />
            fine steps
          </label>

          <div style={{ opacity: 0.5, fontSize: 10, marginBottom: 8, fontVariantNumeric: 'tabular-nums' }}>
            {cur.pos.map(r2).join(', ')} · rot {r2(cur.rotY)} · {r3(cur.size)}m
          </div>

          <div style={{ display: 'flex', gap: 5 }}>
            <button disabled={busy}
              style={{ ...btn, flex: 1, background: isDirty ? '#ffb25e' : 'rgba(255,178,94,0.14)', color: isDirty ? '#1c1206' : '#ffd27d' }}
              onClick={save}>{busy ? '…' : isDirty ? 'Save *' : 'Save'}</button>
            <button style={{ ...btn, width: 52 }} onClick={remove} disabled={busy}>del</button>
          </div>
          {msg && <div style={{ fontSize: 10.5, marginTop: 8, opacity: 0.85, color: msg === 'saved' ? '#9dff7d' : '#ff9d7d' }}>{msg}</div>}
        </>
      )}
    </div>
  );
}
